import React, { useCallback, useEffect, useRef } from 'react';
import { AppBar, Box, Button, Card, CardContent, CircularProgress, Fab, IconButton, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { VovClient } from '../../../ApiOld/ApiServerVov';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { AddAPhoto, Add, ArrowBackIosNew, Send, KeyboardArrowDown, Attachment } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AddMessageRequestDTO, AddMessageRequestEmployeeMobileRequest, MessageWithDateDTO, RequestResponseWithBuildingDTO } from '../../../ApiOld/Api/ApiServerVov';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import MobileRequestItemUpperMenu from './Components/MobileRequestItemUpperMenu';
import MobileCamera from '../Shared/MobileCamera';
import { MobilePhotoDTO } from '../Photos/MobilePhotosScreen';
import { useSnackbar } from 'notistack';
import { ImageDialog } from '../../Shared/ImageDialog';

const MobileRequestDetail: React.FC = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [messages, setMessages] = React.useState<MessageWithDateDTO[]>([]);
    const [requestLocal, setRequestLocal] = React.useState<RequestResponseWithBuildingDTO | undefined>(undefined);
    const [messageText, setMessageText] = React.useState<string>('');
    const [cameraOpen, setCameraOpen] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    const location = useLocation();
    const { request } = location.state ?? {};
    const inputFile = useRef<HTMLInputElement>(null);

    const [imageDialogUrl, setImageDialogUrl] = React.useState<string | null>(null);

    const { authorizedMobileEmployeeApi } = useMobileApi(VovClient);

    const { setModuleButton, setModuleName, setModuleSecondButton, setTitle } = useEmployeeContext();

    const navigate = useNavigate();
    const params = useParams();

    const { enqueueSnackbar } = useSnackbar();
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        console.log(request);
        setRequestLocal(request);

        setTitle && setTitle(request?.text ?? '');

        return () => {
            setTitle && setTitle('');
        }
    }, [request]);

    useEffect(() => {
        if (requestLocal)
            authorizedMobileEmployeeApi.getRequestMessagesEmployeeMobile(requestLocal.id!, 0, 0).then((response) => {
                setMessages(response.messages ?? []);
            });
    }, [requestLocal, authorizedMobileEmployeeApi]);

    useEffect(() => {
        refreshMessages();
    }, []);

    useEffect(() => {
        setModuleButton && setModuleButton(
            <IconButton onClick={() => {
                navigate('/' + params['lang'] + '/' + params['domain'] + '/employee/requests');
            }} >
                <ArrowBackIosNew />
            </IconButton>
        );
    }, [setModuleButton, setModuleName, t, setModuleSecondButton]);

    useEffect(() => {
        if (scrollRef.current) {
            // Scroll to the bottom of the container
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);


    const refreshMessages = useCallback(() => {
        setMessages([]);
        if (requestLocal)
            authorizedMobileEmployeeApi.getRequestMessagesEmployeeMobile(requestLocal.id!, 0, 0).then((response) => {
                setMessages(response.messages ?? []);

            });
    }, [requestLocal, authorizedMobileEmployeeApi, setMessages]);

    const [isButtonVisible, setIsButtonVisible] = React.useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (scrollRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

                // Show button if not at the bottom, hide if at the bottom
                const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
                setIsButtonVisible(!isAtBottom);
            }
        };

        // Add scroll listener to the container
        const container = scrollRef.current;
        container?.addEventListener("scroll", handleScroll);

        return () => {
            container?.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const handleScrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
        }
    };

    const handleImageUpload = (photo: MobilePhotoDTO) => {
        setLoading(true);
        authorizedMobileEmployeeApi.addMessageRequestEmployeeMobile(
            new AddMessageRequestEmployeeMobileRequest({
                message: new AddMessageRequestDTO({
                    requestId: requestLocal!.id!,
                    messageText: '',
                    messageFile: '',
                    messageFileName: '',
                    messageImage: photo.image
                })
            })
        ).then((res) => {

            if (res?.message) {
                setMessageText('');
                refreshMessages();
            }
            else {
                enqueueSnackbar(t('errorSendingMessage') /* + ': ' + res.message */, { variant: 'error' });
            }
        })
            .finally(() => {
                setLoading(false);
            });
    };

    if (cameraOpen) {
        return (<MobileCamera
            allowUpload
            allowMultiple={false}
            defaultCameraType='environment'
            onConfirm={(photos: MobilePhotoDTO[]) => {
                // setImageQueue(prevQueue => [...prevQueue, ...(Array.isArray(photos) ? photos : [photos])]);
                setCameraOpen(false);
                handleImageUpload(photos[0]);
                // setStartUpload(true);
            }}
            onBackButtonClicked={() => setCameraOpen(false)}
        />);
    }
    else if (requestLocal)
        return (
            <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 60px)", backgroundColor: "#f9f9f9" }}>
                <MobileRequestItemUpperMenu request={requestLocal} setRequest={setRequestLocal} />
                <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: "auto", padding: 2 }}>
                    {messages.map((message, index) => (
                        <React.Fragment key={'request' + index}>
                            {/* Date Separator */}
                            {(index === 0 || messages[index - 1].datetime !== message.datetime) && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        margin: "16px 0", // Keep the margin for spacing
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            textAlign: "center",
                                            backgroundColor: "#f4b400",
                                            color: "black",
                                            borderRadius: "16px",
                                            padding: "4px 12px",
                                            display: "inline-block",
                                        }}
                                    >
                                        {message.datetimeDate?.toLocaleDateString(i18n.language)}
                                    </Typography>
                                </Box>
                            )}
                            {/* Message */}
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", marginBottom: 1 }}>
                                <Card sx={{ backgroundColor: "#fff", marginBottom: 0 }}>
                                    <CardContent>
                                        <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                                            <Typography variant="subtitle2" sx={{ color: "#f4b400" }}>
                                                {message.senderName}
                                            </Typography>
                                            {message.read !== true && <Box
                                                ml={1}
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                                width="18px"
                                                height="18px"
                                                borderRadius="50%"
                                                sx={{ backgroundColor: '#FFC600', color: 'white', fontSize: '10px', fontWeight: 'bold' }}
                                            >
                                            </Box>}
                                        </Box>
                                        <Typography variant="body1">{message.messageText}</Typography>
                                        {message.imageUrl && (
                                            <Box sx={{ display: "flex", justifyContent: "center" }} onClick={() => setImageDialogUrl(message.imageUrl!)}>
                                                <img
                                                    src={message.imageUrl}
                                                    alt={'....'}
                                                    style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "8px" }}
                                                />
                                            </Box>
                                        )}
                                        {message.fileUrl && (
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                <Attachment />
                                                <a
                                                    href={message.fileUrl}
                                                    style={{ maxWidth: "100%", maxHeight: "200px", borderRadius: "8px" }}
                                                >{message.fileName}</a>
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: -2 }}>
                                            <Typography variant="caption" sx={{ color: "lightgray" }}>
                                                {message.datetimeDate?.toLocaleTimeString()}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Box>
                        </React.Fragment>
                    ))}
                </Box>

                {/* Input Box */}
                {!loading && <Box sx={{ display: "flex", alignItems: "center", padding: 1, borderTop: "1px solid #ddd" }}>
                    <IconButton onClick={() => {
                        inputFile.current?.click();
                    }}>
                        <Add />
                    </IconButton>
                    <TextField
                        placeholder=""
                        fullWidth
                        variant="outlined"
                        sx={{ backgroundColor: "#fff", borderRadius: "8px" }}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                    />
                    {messageText.length === 0 && <IconButton onClick={() => {
                        setCameraOpen(true);
                    }}>
                        <AddAPhoto />
                    </IconButton>}
                    {messageText.length > 0 && <IconButton onClick={() => {
                        setLoading(true);
                        authorizedMobileEmployeeApi.addMessageRequestEmployeeMobile(new AddMessageRequestEmployeeMobileRequest({ message: new AddMessageRequestDTO({ requestId: requestLocal.id!, messageText: messageText, messageFile: '', messageFileName: '', messageImage: '' }) })).then((response) => {
                            setMessageText('');
                            refreshMessages();
                        }).finally(() => {
                            setLoading(false);
                        });
                    }}>
                        <Send />
                    </IconButton>}

                    <input
                        accept="*/*"
                        onChange={async (e) => {
                            if (e.target && e.target.files) {
                                const file = e.target.files[0];
                                if (!file) return;

                                const reader = new FileReader();
                                reader.onload = async () => {
                                    const base64String = reader.result as string;

                                    // Ověříme, že jde o JPEG, jinak normálně
                                    if (!file.type.toLowerCase().includes('jpeg') && !file.type.toLowerCase().includes('jpg')) {
                                        setLoading(true);
                                        authorizedMobileEmployeeApi.addMessageRequestEmployeeMobile(new AddMessageRequestEmployeeMobileRequest({ message: new AddMessageRequestDTO({ requestId: requestLocal.id!, messageText: '', messageFile: base64String, messageFileName: file.name, messageImage: '' }) })).then((response) => {
                                            setMessageText('');
                                            refreshMessages();
                                            if (inputFile.current) inputFile.current.value = '';
                                        }).finally(() => {
                                            setLoading(false);
                                        }
                                        );
                                        return;
                                    }

                                    authorizedMobileEmployeeApi.addMessageRequestEmployeeMobile(
                                        new AddMessageRequestEmployeeMobileRequest({
                                            message: new AddMessageRequestDTO({
                                                requestId: requestLocal.id!,
                                                messageText: '',
                                                messageFile: '',
                                                messageFileName: '',
                                                messageImage: base64String,
                                            })
                                        })
                                    ).then(() => {
                                        setMessageText('');
                                        refreshMessages();
                                        if (inputFile.current) inputFile.current.value = '';
                                    }).finally(() => {
                                        setLoading(false);
                                    }
                                    );

                                    /* const img = new Image();
                                    img.src = base64String;
                                    img.onload = async () => {
                                        // Oprava orientace obrázku podle EXIF dat
                                        const rotatedBase64 = await fixImageOrientation(img, orientation);

                                        setLoading(true);
                                        // Odeslání zprávy s upraveným obrázkem
                                        authorizedMobileEmployeeApi.addMessageRequestEmployeeMobile(
                                            new AddMessageRequestEmployeeMobileRequest({
                                                message: new AddMessageRequestDTO({
                                                    requestId: requestLocal.id!,
                                                    messageText: '',
                                                    messageFile: '',
                                                    messageFileName: '',
                                                    messageImage: rotatedBase64
                                                })
                                            })
                                        ).then(() => {
                                            setMessageText('');
                                            refreshMessages();
                                            if (inputFile.current) inputFile.current.value = '';
                                        }).finally(() => {
                                            setLoading(false);
                                        }
                                        );
                                    }; */
                                };
                                reader.readAsDataURL(file);
                            }
                        }}
                        type="file"
                        id="file"
                        ref={inputFile}
                        style={{ display: 'none' }}
                    />

                </Box>}
                {loading && <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 1, borderTop: "1px solid #ddd" }}>
                    <CircularProgress />
                </Box>}
                {isButtonVisible && (
                    <Fab
                        color="primary"
                        size="medium"
                        sx={{
                            position: "fixed",
                            bottom: 100,
                            right: 16,
                            zIndex: 1000,
                        }}
                        onClick={handleScrollToBottom}
                    >
                        <KeyboardArrowDown />
                    </Fab>
                )}
                <ImageDialog open={imageDialogUrl !== null} onClose={() => setImageDialogUrl(null)} imageUrl={imageDialogUrl} />
            </Box>
        );
    else
        return <>{t('loading...')}</>;
};

export default MobileRequestDetail;